import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, VendorTag } from "../components/layout/AppLayout";
import { Button } from "../components/ui/button";
import { ArrowsClockwise, Globe, CaretDown, CaretRight, PaperPlaneTilt, PencilSimple, X, Check } from "@phosphor-icons/react";
import { toast } from "sonner";
import { VENDOR_CATEGORIES, engineerMatchesVendorFilter } from "../lib/vendorCategories";

// Roster hours are returned by the backend anchored to Sydney AEST (UTC+10).
// We convert those hour labels client-side for any target timezone.
const TIMEZONES = [
  { key: "sydney", label: "Sydney", abbr: "AEST", offset: 10 },
  { key: "kolkata", label: "Kolkata", abbr: "IST", offset: 5.5 },
  { key: "manila", label: "Manila", abbr: "PHT", offset: 8 },
  { key: "auckland", label: "Auckland", abbr: "NZST", offset: 12 },
  { key: "singapore", label: "Singapore", abbr: "SGT", offset: 8 },
  { key: "dubai", label: "Dubai", abbr: "GST", offset: 4 },
  { key: "london", label: "London", abbr: "GMT", offset: 0 },
  { key: "new_york", label: "New York", abbr: "EST", offset: -5 },
];

const SYDNEY_OFFSET = 10; // Base offset used by backend hours

// Convert a Sydney hour (0-23) into a target-tz clock string like "4:30a" or "9p".
function convertHourLabel(sydneyHour, targetOffset) {
  const diff = targetOffset - SYDNEY_OFFSET; // hours to add to Sydney
  // Support half-hour offsets (e.g., IST +5:30)
  const raw = sydneyHour + diff;
  // normalize into [0, 24)
  const total = ((raw % 24) + 24) % 24;
  const hour = Math.floor(total);
  const minutes = Math.round((total - hour) * 60);
  const mm = minutes === 0 ? "" : `:${String(minutes).padStart(2, "0")}`;
  if (hour === 0) return `12${mm}a`;
  if (hour < 12) return `${hour}${mm}a`;
  if (hour === 12) return `12${mm}p`;
  return `${hour - 12}${mm}p`;
}

export default function Roster() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [primaryTz, setPrimaryTz] = useState("sydney");
  const [secondaryTz, setSecondaryTz] = useState("kolkata");
  const [compareOn, setCompareOn] = useState(true);
  const [viewMode, setViewMode] = useState("hourly");  // 'hourly' | 'weekly'
  // Date range for multi-week view. Default: today → +28 days.
  const today = new Date().toISOString().slice(0, 10);
  const plus28 = new Date(Date.now() + 28 * 86400000).toISOString().slice(0, 10);
  const [rangeStart, setRangeStart] = useState(today);
  const [rangeEnd, setRangeEnd] = useState(plus28);
  const [multiWeek, setMultiWeek] = useState(null);
  const [multiLoading, setMultiLoading] = useState(false);
  const [reminderBusy, setReminderBusy] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [currentRole, setCurrentRole] = useState(null);
  useEffect(() => {
    api.get("/auth/me").then(r => setCurrentRole(r.data?.role || null)).catch(() => setCurrentRole(null));
  }, []);
  const isAdmin = currentRole === "admin";
  // Skill filter — same category/tech scheme as Talent Pool
  const [selectedCats, setSelectedCats] = useState(() => new Set());
  const [selectedTechs, setSelectedTechs] = useState(() => new Set());
  const [expanded, setExpanded] = useState(() => new Set());
  const toggleSet = (setter, value) => setter((prev) => {
    const next = new Set(prev);
    next.has(value) ? next.delete(value) : next.add(value);
    return next;
  });
  const clearSkillFilters = () => { setSelectedCats(new Set()); setSelectedTechs(new Set()); setExpanded(new Set()); };
  const skillFilterActive = selectedCats.size + selectedTechs.size;

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/roster/hourly", { params: { date } });
      setData(r.data);
    } catch { toast.error("Failed to load roster"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date]);

  const loadMultiWeek = async () => {
    if (rangeEnd < rangeStart) { toast.error("End date must be on or after start date"); return; }
    setMultiLoading(true);
    try {
      const r = await api.get("/roster/multi-week", { params: { start_date: rangeStart, end_date: rangeEnd } });
      setMultiWeek(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load multi-week roster");
    }
    finally { setMultiLoading(false); }
  };
  useEffect(() => {
    if (viewMode === "weekly") loadMultiWeek();
    /* eslint-disable-next-line */
  }, [viewMode, rangeStart, rangeEnd]);

  const primary = useMemo(() => TIMEZONES.find(t => t.key === primaryTz) || TIMEZONES[0], [primaryTz]);
  const secondary = useMemo(() => TIMEZONES.find(t => t.key === secondaryTz) || TIMEZONES[1], [secondaryTz]);

  if (loading) return <div className="text-xs font-mono uppercase tracking-wider text-slate-500">Loading roster…</div>;
  if (!data) return <div className="text-xs font-mono uppercase tracking-wider text-slate-500">No data</div>;

  const engineers = data.personnel
    .filter((p) => (p.person_type || []).includes("engineer"))
    .filter((p) => engineerMatchesVendorFilter(p.vendors || [], selectedCats, selectedTechs));
  const fieldTechs = data.personnel
    .filter((p) => (p.person_type || []).includes("field_tech") && !(p.person_type || []).includes("engineer"))
    .filter((p) => engineerMatchesVendorFilter(p.vendors || [], selectedCats, selectedTechs));

  // Multi-week people (uses same skill filter)
  const multiEngineers = (multiWeek?.personnel || [])
    .filter((p) => (p.person_type || []).includes("engineer"))
    .filter((p) => engineerMatchesVendorFilter(p.vendors || [], selectedCats, selectedTechs));
  const multiFieldTechs = (multiWeek?.personnel || [])
    .filter((p) => (p.person_type || []).includes("field_tech") && !(p.person_type || []).includes("engineer"))
    .filter((p) => engineerMatchesVendorFilter(p.vendors || [], selectedCats, selectedTechs));

  const sendReminder = async (pid, weekStart) => {
    const key = `${pid}-${weekStart}`;
    setReminderBusy(key);
    try {
      const r = await api.post(`/roster/${pid}/remind-week`, null, { params: { week_start: weekStart } });
      if (r.data?.sent) toast.success("Reminder sent");
      else toast.warning("Reminder recorded but email not delivered — check email service config");
      loadMultiWeek();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to send reminder";
      if (e?.response?.status === 429) toast.info(msg);
      else toast.error(msg);
    } finally { setReminderBusy(null); }
  };

  const subtitle = compareOn
    ? `Coverage window shown simultaneously in ${primary.label} (${primary.abbr}) and ${secondary.label} (${secondary.abbr}). Green = person is available in that hour block.`
    : `Coverage window shown in ${primary.label} (${primary.abbr}). Green = person is available in that hour block.`;

  return (
    <div>
      <PageHeader
        overline="Scheduling"
        title="Hourly Roster"
        subtitle={subtitle}
        actions={
          <div className="flex gap-2">
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
              className="h-9 px-2 rounded border border-slate-300 bg-white text-xs font-mono" />
            <Button data-testid="roster-refresh" onClick={load} variant="outline" className="border-slate-300 bg-white text-[#0F1E38] font-mono uppercase tracking-wider text-xs">
              <ArrowsClockwise size={14} className="mr-2" /> Refresh
            </Button>
          </div>
        }
      />

      {/* Timezone selector */}
      <div className="mb-4 border border-slate-200 bg-white rounded-lg p-3 flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.24em] text-slate-500">
          <Globe size={14} /> Timezone
        </div>
        <div className="flex items-center gap-2">
          <label className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Primary</label>
          <select
            data-testid="roster-tz-primary"
            value={primaryTz}
            onChange={(e) => setPrimaryTz(e.target.value)}
            className="h-8 px-2 rounded border border-slate-300 bg-white text-xs font-mono text-[#0F1E38]"
          >
            {TIMEZONES.map(tz => (
              <option key={tz.key} value={tz.key}>{tz.label} ({tz.abbr})</option>
            ))}
          </select>
        </div>
        <label className="flex items-center gap-1.5 text-[11px] text-slate-600 font-mono cursor-pointer">
          <input
            data-testid="roster-tz-compare"
            type="checkbox"
            checked={compareOn}
            onChange={(e) => setCompareOn(e.target.checked)}
          />
          Compare
        </label>
        {compareOn && (
          <div className="flex items-center gap-2">
            <label className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Secondary</label>
            <select
              data-testid="roster-tz-secondary"
              value={secondaryTz}
              onChange={(e) => setSecondaryTz(e.target.value)}
              className="h-8 px-2 rounded border border-slate-300 bg-white text-xs font-mono text-[#0F1E38]"
            >
              {TIMEZONES.filter(tz => tz.key !== primaryTz).map(tz => (
                <option key={tz.key} value={tz.key}>{tz.label} ({tz.abbr})</option>
              ))}
            </select>
          </div>
        )}
        <div className="ml-auto text-[10px] font-mono text-slate-400">
          Data anchored to Sydney AEST (UTC+10)
        </div>
      </div>

      <div className="mb-4 flex flex-wrap gap-3 items-center text-[11px] text-slate-500">
        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-3.5 h-3.5 rounded-sm bg-emerald-500"></span> Available</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-3.5 h-3.5 rounded-sm bg-slate-100 border border-slate-300"></span> Not available / not submitted</span>
      </div>

      {/* View toggle + skill filter */}
      <div className="mb-4 border border-slate-200 bg-white rounded-lg p-3 space-y-3">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">View</span>
            <div className="inline-flex rounded border border-slate-300 overflow-hidden">
              <button data-testid="roster-view-hourly" type="button" onClick={() => setViewMode("hourly")}
                className={`px-3 py-1.5 text-xs font-mono uppercase ${viewMode === "hourly" ? "bg-[#FFC107] text-[#0F1E38]" : "bg-white text-slate-500 hover:bg-slate-50"}`} style={{transition:"background-color 0.15s ease"}}>Hourly</button>
              <button data-testid="roster-view-weekly" type="button" onClick={() => setViewMode("weekly")}
                className={`px-3 py-1.5 text-xs font-mono uppercase border-l border-slate-300 ${viewMode === "weekly" ? "bg-[#FFC107] text-[#0F1E38]" : "bg-white text-slate-500 hover:bg-slate-50"}`} style={{transition:"background-color 0.15s ease"}}>Multi-Week</button>
            </div>
          </div>
          {viewMode === "weekly" && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">From</span>
              <input
                data-testid="roster-range-start"
                type="date"
                value={rangeStart}
                onChange={(e) => setRangeStart(e.target.value)}
                className="h-8 px-2 rounded border border-slate-300 bg-white text-xs font-mono text-[#0F1E38]"
              />
              <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">To</span>
              <input
                data-testid="roster-range-end"
                type="date"
                value={rangeEnd}
                onChange={(e) => setRangeEnd(e.target.value)}
                className="h-8 px-2 rounded border border-slate-300 bg-white text-xs font-mono text-[#0F1E38]"
              />
              <div className="flex gap-1">
                <button type="button" data-testid="range-preset-14" onClick={() => { const t=new Date().toISOString().slice(0,10); setRangeStart(t); setRangeEnd(new Date(Date.now()+14*86400000).toISOString().slice(0,10)); }} className="px-2 py-1 text-[10px] font-mono uppercase border border-slate-300 bg-white text-slate-600 hover:bg-slate-50 rounded">14d</button>
                <button type="button" data-testid="range-preset-30" onClick={() => { const t=new Date().toISOString().slice(0,10); setRangeStart(t); setRangeEnd(new Date(Date.now()+30*86400000).toISOString().slice(0,10)); }} className="px-2 py-1 text-[10px] font-mono uppercase border border-slate-300 bg-white text-slate-600 hover:bg-slate-50 rounded">30d</button>
                <button type="button" data-testid="range-preset-60" onClick={() => { const t=new Date().toISOString().slice(0,10); setRangeStart(t); setRangeEnd(new Date(Date.now()+60*86400000).toISOString().slice(0,10)); }} className="px-2 py-1 text-[10px] font-mono uppercase border border-slate-300 bg-white text-slate-600 hover:bg-slate-50 rounded">60d</button>
              </div>
            </div>
          )}
          <div className="h-5 w-px bg-slate-300" />
          <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">Skill</span>
          <div className="flex flex-wrap gap-2 flex-1">
            {VENDOR_CATEGORIES.map((c) => {
              const isSel = selectedCats.has(c.key);
              const isExp = expanded.has(c.key);
              const hasChildren = c.techs.length > 1;
              return (
                <div key={c.key} className="inline-flex items-stretch rounded border border-slate-300 bg-white overflow-hidden">
                  <button type="button" data-testid={`roster-cat-${c.key}`} onClick={() => toggleSet(setSelectedCats, c.key)}
                    className={`px-2.5 py-1 text-[11px] font-mono uppercase ${isSel ? "bg-[#FFC107] text-[#0F1E38]" : "text-slate-600 hover:bg-slate-50"}`} style={{transition:"background-color 0.15s ease"}}>
                    {c.label}{isSel && <span className="ml-1 text-[9px]">✓</span>}
                  </button>
                  {hasChildren && (
                    <button type="button" onClick={() => toggleSet(setExpanded, c.key)}
                      className={`px-1.5 border-l border-slate-300 ${isExp ? "bg-slate-100" : "hover:bg-slate-50"}`} style={{transition:"background-color 0.15s ease"}}
                      title={isExp ? "Collapse" : "Expand"}>
                      {isExp ? <CaretDown size={9} /> : <CaretRight size={9} />}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          {skillFilterActive > 0 && (
            <button data-testid="roster-clear-skills" onClick={clearSkillFilters} className="text-[10px] font-mono uppercase text-slate-500 hover:text-red-600 whitespace-nowrap" style={{transition:"color 0.15s ease"}}>Clear ({skillFilterActive})</button>
          )}
        </div>
        {VENDOR_CATEGORIES.some((c) => expanded.has(c.key) && c.techs.length > 1) && (
          <div className="pl-3 border-l-2 border-amber-200 space-y-2">
            {VENDOR_CATEGORIES.filter((c) => expanded.has(c.key) && c.techs.length > 1).map((c) => (
              <div key={`exp-${c.key}`}>
                <div className="text-[10px] font-mono uppercase text-slate-400 mb-1">{c.label} products</div>
                <div className="flex flex-wrap gap-1.5">
                  {c.techs.map((t) => (
                    <button key={t} type="button" onClick={() => toggleSet(setSelectedTechs, t)}
                      className={`px-2 py-0.5 rounded text-[11px] border ${selectedTechs.has(t) ? "bg-amber-100 border-amber-400 text-[#0F1E38] font-semibold" : "bg-white border-slate-300 text-slate-500 hover:border-slate-400"}`} style={{transition:"all 0.15s ease"}}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {viewMode === "hourly" ? (
        <>
          <RosterHourlySection title="Engineers" people={engineers} hours={data.hours} primary={primary} secondary={compareOn ? secondary : null} />
          <RosterHourlySection title="Field Technicians" people={fieldTechs} hours={data.hours} primary={primary} secondary={compareOn ? secondary : null} />
        </>
      ) : (
        <>
          <div className="mb-4 flex flex-wrap gap-3 items-center text-[11px] text-slate-500">
            <span className="inline-flex items-center gap-1.5"><span className="inline-block w-3.5 h-3.5 rounded-sm bg-emerald-500"></span> Available (has free days)</span>
            <span className="inline-flex items-center gap-1.5"><span className="inline-block w-3.5 h-3.5 rounded-sm bg-rose-500"></span> Blocked (all days unavailable)</span>
            <span className="inline-flex items-center gap-1.5"><span className="inline-block w-3.5 h-3.5 rounded-sm bg-slate-200 border border-slate-300"></span> Not filled — click "Remind"</span>
          </div>
          {multiLoading && <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-4">Loading multi-week view…</div>}
          <MultiWeekSection
            title="Engineers"
            people={multiEngineers}
            weeks={multiWeek?.weeks || []}
            onRemind={sendReminder}
            onEdit={isAdmin ? (person, week) => setEditTarget({ person, week }) : null}
            reminderBusy={reminderBusy}
            isAdmin={isAdmin}
          />
          <MultiWeekSection
            title="Field Technicians"
            people={multiFieldTechs}
            weeks={multiWeek?.weeks || []}
            onRemind={sendReminder}
            onEdit={isAdmin ? (person, week) => setEditTarget({ person, week }) : null}
            reminderBusy={reminderBusy}
            isAdmin={isAdmin}
          />
        </>
      )}
      {editTarget && (
        <AvailabilityEditModal
          person={editTarget.person}
          week={editTarget.week}
          onClose={() => setEditTarget(null)}
          onSaved={() => { setEditTarget(null); loadMultiWeek(); }}
        />
      )}
    </div>
  );
}

function MultiWeekSection({ title, people, weeks, onRemind, onEdit, reminderBusy, isAdmin }) {
  if (!weeks || weeks.length === 0) {
    return null;
  }
  if (!people || people.length === 0) {
    return (
      <div className="mb-6">
        <div className="overline text-slate-700 mb-2">{title}</div>
        <div className="grid-bg py-10 text-center text-xs text-slate-500">No {title.toLowerCase()} match the current filters.</div>
      </div>
    );
  }
  return (
    <div className="mb-6">
      <div className="overline text-slate-700 mb-2">{title} <span className="font-mono text-[10px] text-slate-400">· {people.length} shown</span></div>
      <div className="border border-slate-200 bg-white rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid={`roster-multiweek-${title.toLowerCase().replace(/\s+/g, "-")}`}>
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="sticky left-0 z-10 bg-slate-50 border-r border-slate-200 px-4 py-3 text-left overline whitespace-nowrap min-w-[200px]">Name</th>
                <th className="px-3 py-3 text-left overline whitespace-nowrap">Skills</th>
                {weeks.map((w) => (
                  <th key={w.index} className="px-3 py-3 text-center overline whitespace-nowrap min-w-[160px]">
                    <div className="text-[11px] text-[#0F1E38] font-semibold">{w.label}</div>
                    <div className="text-[10px] font-mono text-slate-400 lowercase tracking-normal normal-case">{w.range_label}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {people.map((p) => (
                <tr key={p.id} data-testid={`roster-mw-row-${p.id}`} className="border-b border-slate-100 hover:bg-slate-50/60" style={{transition:"background-color 0.15s ease"}}>
                  <td className="sticky left-0 z-10 bg-white border-r border-slate-200 px-4 py-3 whitespace-nowrap">
                    <div className="font-medium text-[#0F1E38]">{p.full_name}</div>
                    <div className="text-[11px] text-slate-500 font-mono">{p.region || "—"}</div>
                    {p.site_ready === false && (
                      <div className="inline-flex items-center mt-1 px-1 py-0.5 rounded bg-red-50 border border-red-200 text-red-700 text-[9px] font-mono uppercase tracking-wider">⚠ NOT CLEARED</div>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex flex-wrap gap-1 max-w-[280px]">
                      {(p.vendors || []).slice(0, 6).map((v) => <VendorTag key={v} vendor={v} />)}
                      {(p.vendors || []).length > 6 && <span className="text-[10px] text-slate-400 font-mono">+{p.vendors.length - 6}</span>}
                      {(!p.vendors || p.vendors.length === 0) && <span className="text-[11px] text-slate-400 italic">—</span>}
                    </div>
                  </td>
                  {p.weeks.map((cell) => (
                    <WeekCell
                      key={cell.week_start}
                      cell={cell}
                      personId={p.id}
                      personName={p.full_name}
                      personEmail={p.email}
                      onRemind={onRemind}
                      onEdit={onEdit ? () => onEdit(p, cell) : null}
                      busy={reminderBusy === `${p.id}-${cell.week_start}`}
                      isAdmin={isAdmin}
                    />
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function WeekCell({ cell, personId, personName, personEmail, onRemind, onEdit, busy, isAdmin }) {
  const { status, days_free, days_blocked, reminder_sent_at, week_start } = cell;
  let pillClass = "", label = "", sub = "";
  if (status === "available") {
    pillClass = "bg-emerald-50 border-emerald-300 text-emerald-800";
    label = `${days_free} day${days_free === 1 ? "" : "s"} free`;
    sub = days_blocked ? `${days_blocked} blocked` : "";
  } else if (status === "blocked") {
    pillClass = "bg-rose-50 border-rose-300 text-rose-800";
    label = "Blocked";
    sub = `${days_blocked} day${days_blocked === 1 ? "" : "s"} unavailable`;
  } else {
    pillClass = "bg-slate-100 border-slate-300 text-slate-500";
    label = "Not filled";
    sub = reminder_sent_at ? "Reminder sent" : "";
  }
  return (
    <td className="px-3 py-3 text-center align-middle">
      <div className={`inline-flex flex-col items-center gap-1 px-2.5 py-1.5 rounded border ${pillClass} min-w-[110px]`}>
        <div className="text-xs font-semibold">{label}</div>
        {sub && <div className="text-[9px] font-mono uppercase tracking-wider opacity-70">{sub}</div>}
      </div>
      <div className="mt-1.5 flex items-center justify-center gap-1">
        {status === "not_filled" && (
          <button
            data-testid={`remind-${personId}-${week_start}`}
            type="button"
            disabled={busy || !!reminder_sent_at || !personEmail}
            onClick={() => onRemind(personId, week_start)}
            title={!personEmail ? "No email on file" : (reminder_sent_at ? `Reminder already sent at ${reminder_sent_at}` : `Email ${personName} about this week`)}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-mono uppercase border border-amber-400 bg-amber-50 text-amber-800 hover:bg-amber-100 disabled:opacity-40 disabled:cursor-not-allowed"
            style={{transition:"background-color 0.15s ease"}}
          >
            <PaperPlaneTilt size={10} />
            {busy ? "Sending…" : (reminder_sent_at ? "Sent" : "Remind")}
          </button>
        )}
        {isAdmin && onEdit && (
          <button
            data-testid={`edit-avail-${personId}-${week_start}`}
            type="button"
            onClick={onEdit}
            title="Admin: edit availability for this week"
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-mono uppercase border border-slate-300 bg-white text-slate-600 hover:bg-slate-50"
            style={{transition:"background-color 0.15s ease"}}
          >
            <PencilSimple size={10} /> Edit
          </button>
        )}
      </div>
    </td>
  );
}

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function AvailabilityEditModal({ person, week, onClose, onSaved }) {
  // Build day rows across the whole range (handles partial last chunks too)
  const days = useMemo(() => {
    const start = new Date(week.week_start + "T00:00:00");
    const end = new Date(week.week_end + "T00:00:00");
    const list = [];
    let d = new Date(start);
    while (d <= end) {
      list.push(d.toISOString().slice(0, 10));
      d = new Date(d); d.setDate(d.getDate() + 1);
    }
    return list;
  }, [week.week_start, week.week_end]);

  // Pre-populate from stored entries sent along in the week cell
  const entryByDate = useMemo(() => {
    const map = {};
    (week.entries || []).forEach((e) => { if (e.date) map[e.date] = e; });
    return map;
  }, [week.entries]);

  const dayOfWeekLabel = (iso) => {
    const dow = new Date(iso + "T00:00:00").getDay();  // 0 = Sun
    return WEEKDAYS[(dow + 6) % 7];  // shift so Mon = 0
  };

  const [rows, setRows] = useState(() => days.map((d) => {
    const e = entryByDate[d];
    if (!e) return { date: d, mode: "skip", from: "", to: "" };
    if (e.unavailable || (e.unavailable_from && !e.available_from)) {
      return { date: d, mode: "unavailable", from: e.unavailable_from || "", to: e.unavailable_to || "" };
    }
    if (e.available_from) {
      return { date: d, mode: "available", from: e.available_from, to: e.available_to || "" };
    }
    return { date: d, mode: "skip", from: "", to: "" };
  }));
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  const updateRow = (idx, patch) => setRows((prev) => prev.map((r, i) => i === idx ? { ...r, ...patch } : r));

  const loadHistory = async () => {
    try {
      const r = await api.get(`/roster/${person.id}/override-history`, { params: { limit: 20 } });
      setHistory(r.data?.entries || []);
    } catch { toast.error("Failed to load history"); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const entries = rows.map((r) => {
        if (r.mode === "skip") return { date: r.date };
        if (r.mode === "available") return { date: r.date, available_from: r.from, available_to: r.to };
        if (r.mode === "unavailable") {
          // Allow a "whole day unavailable" entry even without times
          const payload = { date: r.date, unavailable: true };
          if (r.from) payload.unavailable_from = r.from;
          if (r.to) payload.unavailable_to = r.to;
          return payload;
        }
        return { date: r.date };
      });
      await api.put(`/roster/${person.id}/availability-override`, { entries, comment: comment.trim() || undefined });
      toast.success(`Availability saved for ${person.full_name}`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save availability");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[92vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between">
          <div>
            <div className="overline text-slate-500">Admin availability override</div>
            <div className="text-lg font-semibold text-[#0F1E38]">{person.full_name}</div>
            <div className="text-xs font-mono text-slate-500">{week.label} · {week.range_label}</div>
          </div>
          <button data-testid="edit-avail-close" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
        </div>
        <div className="px-5 py-4 overflow-y-auto flex-1">
          <div className="text-[11px] text-slate-500 mb-3">
            Pre-filled with the engineer's current availability. Any changes you save <strong>replace</strong> their existing entries for these dates. All overrides are logged.
          </div>
          <table className="w-full text-sm">
            <thead className="text-left overline border-b border-slate-200">
              <tr>
                <th className="py-2">Day</th>
                <th>Status</th>
                <th>From</th>
                <th>To</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.date} className="border-b border-slate-100">
                  <td className="py-2 pr-2">
                    <div className="font-medium text-[#0F1E38]">{dayOfWeekLabel(r.date)}</div>
                    <div className="text-[10px] font-mono text-slate-400">{r.date}</div>
                  </td>
                  <td className="pr-2">
                    <select
                      data-testid={`edit-row-mode-${i}`}
                      value={r.mode}
                      onChange={(e) => updateRow(i, { mode: e.target.value })}
                      className="h-8 px-2 rounded border border-slate-300 bg-white text-xs font-mono"
                    >
                      <option value="available">Available</option>
                      <option value="unavailable">Unavailable</option>
                      <option value="skip">Clear / Skip</option>
                    </select>
                  </td>
                  <td className="pr-2">
                    <input
                      data-testid={`edit-row-from-${i}`}
                      type="time"
                      value={r.from}
                      disabled={r.mode === "skip"}
                      onChange={(e) => updateRow(i, { from: e.target.value })}
                      className="h-8 px-2 rounded border border-slate-300 bg-white text-xs font-mono disabled:bg-slate-50 disabled:text-slate-400"
                    />
                  </td>
                  <td>
                    <input
                      data-testid={`edit-row-to-${i}`}
                      type="time"
                      value={r.to}
                      disabled={r.mode === "skip"}
                      onChange={(e) => updateRow(i, { to: e.target.value })}
                      className="h-8 px-2 rounded border border-slate-300 bg-white text-xs font-mono disabled:bg-slate-50 disabled:text-slate-400"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="mt-4">
            <label className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500 block mb-1">Comment (optional)</label>
            <textarea
              data-testid="edit-avail-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="e.g. Called Alex — confirmed off on Thu for personal reason, available rest of the week."
              rows={2}
              className="w-full px-3 py-2 rounded border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
            />
          </div>

          <div className="mt-4 border-t border-slate-200 pt-3">
            <button
              data-testid="edit-avail-history-toggle"
              type="button"
              onClick={() => { if (!historyOpen) loadHistory(); setHistoryOpen(!historyOpen); }}
              className="text-[11px] font-mono uppercase tracking-wider text-slate-500 hover:text-[#0F1E38] inline-flex items-center gap-1"
            >
              {historyOpen ? <CaretDown size={10} /> : <CaretRight size={10} />} Override history
            </button>
            {historyOpen && (
              <div className="mt-2 text-xs text-slate-600 space-y-2 max-h-48 overflow-y-auto" data-testid="edit-avail-history">
                {!history && <div className="text-slate-400 italic">Loading…</div>}
                {history && history.length === 0 && <div className="text-slate-400 italic">No prior overrides.</div>}
                {history && history.map((h) => (
                  <div key={h.id} className="border border-slate-200 rounded px-2 py-1.5 bg-slate-50">
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-[10px] text-[#0F1E38]">{h.admin_email}</span>
                      <span className="font-mono text-[10px] text-slate-400">{(h.created_at || "").slice(0, 16).replace("T", " ")}</span>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {h.dates?.length || 0} date{(h.dates?.length || 0) === 1 ? "" : "s"} · <span className="font-mono">{(h.dates || []).slice(0, 3).join(", ")}{(h.dates || []).length > 3 ? "…" : ""}</span>
                    </div>
                    {h.comment && <div className="mt-1 text-[11px] text-[#0F1E38] italic">"{h.comment}"</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
          <Button data-testid="edit-avail-cancel" onClick={onClose} variant="outline" className="border-slate-300 bg-white text-slate-700 font-mono uppercase tracking-wider text-xs">Cancel</Button>
          <Button data-testid="edit-avail-save" onClick={save} disabled={saving} className="bg-[#0F1E38] text-white hover:bg-[#1a2a4a] font-mono uppercase tracking-wider text-xs">
            <Check size={14} className="mr-1.5" /> {saving ? "Saving…" : "Save Availability"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function RosterHourlySection({ title, people, hours, primary, secondary }) {
  if (!people || people.length === 0) {
    return (
      <div className="mb-6">
        <div className="text-[11px] font-mono uppercase tracking-[0.24em] text-slate-500 mb-2">{title} <span className="text-slate-400">(0)</span></div>
        <div className="border border-dashed border-slate-200 rounded-lg p-8 text-center text-xs text-slate-500 bg-white">No personnel in this category yet.</div>
      </div>
    );
  }
  return (
    <div className="mb-6">
      <div className="text-[11px] font-mono uppercase tracking-[0.24em] text-slate-500 mb-2">{title} <span className="text-slate-400">({people.length})</span></div>
      <div className="border border-slate-200 bg-white rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="text-xs" data-testid={`roster-table-${title.toLowerCase().replace(/\s+/g, "-")}`}>
            <thead className="bg-slate-50">
              <tr>
                <th className="sticky left-0 z-10 bg-slate-50 border-r border-slate-200 px-3 py-2 text-left text-[10px] font-mono uppercase tracking-wider text-slate-500 whitespace-nowrap min-w-[160px]">
                  Person
                </th>
                {hours.map((h, idx) => {
                  const primaryLabel = convertHourLabel(h, primary.offset);
                  const secondaryLabel = secondary ? convertHourLabel(h, secondary.offset) : null;
                  return (
                    <th
                      key={idx}
                      className="px-1 py-2 text-center font-mono text-[10px] uppercase tracking-wider text-slate-500"
                      style={{ minWidth: secondary ? 56 : 40 }}
                    >
                      <div className="text-[10px] text-[#0F1E38] font-semibold">{primaryLabel}</div>
                      <div className="text-[8px] text-slate-400 leading-tight">{primary.abbr}</div>
                      {secondary && (
                        <>
                          <div className="my-0.5 border-t border-dashed border-slate-200 mx-1"></div>
                          <div className="text-[10px] text-amber-600 font-semibold">{secondaryLabel}</div>
                          <div className="text-[8px] text-slate-400 leading-tight">{secondary.abbr}</div>
                        </>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {people.map((p) => (
                <tr key={p.id} data-testid={`roster-h-row-${p.id}`} className="border-t border-slate-100 hover:bg-slate-50/40">
                  <td className="sticky left-0 z-10 bg-white border-r border-slate-200 px-3 py-2 whitespace-nowrap">
                    <div className="flex items-center gap-1.5">
                      <div className="text-sm text-[#0F1E38] font-medium" title={p.full_name}>{p.display_name}</div>
                      {p.site_ready === false && (
                        <span
                          data-testid={`site-warn-${p.id}`}
                          className="inline-flex items-center px-1 py-0.5 rounded bg-red-50 border border-red-200 text-red-700 text-[9px] font-mono uppercase tracking-wider"
                          title={`Not cleared for site — ${(p.site_blockers || []).map(b => b.kind).join(", ")}`}
                        >
                          ⚠ NOT CLEARED
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] text-slate-500 font-mono">{p.region || "—"}</div>
                  </td>
                  {hours.map((h, idx) => {
                    const avail = !!p.hours_available[h];
                    const primaryLabel = convertHourLabel(h, primary.offset);
                    const secondaryLabel = secondary ? convertHourLabel(h, secondary.offset) : null;
                    const tip = secondary
                      ? `${p.full_name} · ${primaryLabel} ${primary.abbr} / ${secondaryLabel} ${secondary.abbr} · ${avail ? "Available" : "Not available"}`
                      : `${p.full_name} · ${primaryLabel} ${primary.abbr} · ${avail ? "Available" : "Not available"}`;
                    return (
                      <td key={idx} className="p-1 text-center align-middle" style={{ minWidth: secondary ? 56 : 40 }}>
                        <div
                          className={`h-6 mx-auto rounded ${avail ? "bg-emerald-500" : "bg-slate-100 border border-slate-200"}`}
                          style={{ width: "80%" }}
                          title={tip}
                        ></div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
